<template>
  <BaseCard
    :result="{
      orgName: searchResult.secondary_organization.name,
      orgLink: searchResult.link,
      tagName: $t(`search.links.tagName`),
      id: searchResult.id,
      type: searchResult.type,
      result_position: resultPosition,
      display_text: searchResult.name,
      category: 'search_result',
    }"
    class="link-card result-card"
    @result-clicked="handleResultClick"
  >
    <template #preview>
      <div class="preview-fallback">
        <Icon
          name="lucide:link"
          size="24"
        />
      </div>
    </template>
    <template #result-content>
      <a
        :href="url"
        :target="isOpenedInNewTab ? '_blank' : '_self'"
        class="title"
        data-testid="link-card-title"
        @click.stop="handleResultClick"
        >{{ searchResult.name }}</a
      >
      <div class="link">{{ formattedURL }}</div>
      <PublishedUpdatedDate :search-result="searchResult" />
    </template>
  </BaseCard>
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import PublishedUpdatedDate from './PublishedUpdatedDate.vue';
import { computed } from 'vue';

const props = defineProps({
  searchResult: {
    type: Object,
    default: () => ({}),
  },
  resultPosition: {
    type: Number,
    required: true,
  },
  /** Overrides `searchResult.content.url` for result types that expose the URL elsewhere. */
  destinationUrl: {
    type: String,
    default: undefined,
  },
  /** Overrides `searchResult.content.opens_in_new_tab`. */
  opensInNewTab: {
    type: Boolean,
    default: undefined,
  },
});

const emit = defineEmits(['result-clicked']);

const url = computed(() => {
  return props.destinationUrl ?? props.searchResult.content?.url;
});

const isOpenedInNewTab = computed(() => {
  return props.opensInNewTab ?? props.searchResult.content?.opens_in_new_tab;
});

const formattedURL = computed(() => {
  const rawUrl = url.value || '';
  return rawUrl.replace(/^(https?:\/\/)?(www\.)?/, '');
});

/**
 * Navigates to the result, honoring its new tab setting for clicks on the title and on the card.
 * The anchor default is prevented so a title click does not also navigate the current tab.
 * @param {MouseEvent} [event] - The click event, absent when the click came from the card body.
 */
async function handleResultClick(event) {
  event?.preventDefault();

  if (isOpenedInNewTab.value) {
    window.open(url.value, '_blank', 'noopener');
  } else {
    await navigateTo(url.value, { external: true });
  }

  emit('result-clicked', {
    position: props.resultPosition,
    url: url.value,
  });
}
</script>

<style scoped>
.link-card {
  --color-blue-500: #1850c1;
  --color-blue-50: #f3f6fc;
  --background-color: var(--color-blue-50);
  --text-color: var(--color-blue-500);
}
.link-card .title {
  text-decoration: none;
  line-height: 120%;
}
.link {
  color: var(--color-text-secondary);
  font-size: 12px;
}
</style>
